import React from "react";
import { Check, TriangleAlert } from "lucide-react";

/**
 * ConfirmedRow — issue #750 § 5.14. A confirmed value with a trailing
 * "Change" link — e.g. the ground confirmed at the top of the scorecard
 * step (#758), with a way back to the ground step (#757).
 *
 * PR #841 review, B1 — `variant="card"` (DirectionA2.dc.html:58): the row
 * as a white card, `padding: 12px 14px`, a 1px `--nw-line` border and a
 * 12px radius (`--nw-radius-card`). The default (`variant="plain"`, the
 * component's own original shape — a bare row, no card) is unchanged, so
 * a future caller that wants the bare row keeps it; #758 is the only
 * caller today and now passes `variant="card"` explicitly.
 */
export function ConfirmedRow({
  eyebrow = null,
  value,
  onChange,
  changeLabel = "Change",
  // Issue #758 — the "Change" link needs its own testid (DirectionA2.dc.html's
  // own `log-step-2-change-ground`, separate from the row's own
  // `log-step-2-ground`), which the first version of this component
  // (issue #750) had no way to carry — optional, so every existing caller
  // that doesn't pass it is unaffected.
  changeTestId,
  // #1299 — extra props for the Change button (e.g. `data-field-invalid` and
  // `aria-describedby` when the confirmed value is the missing field).
  changeButtonProps,
  // #1309 (D303): the confirmed value is the missing field, so the green
  // Check gives way to the red alert glyph (beside the caller's red border).
  invalid = false,
  variant = "plain", // "plain" | "card"
  className = "",
  style,
  "data-testid": testId,
  ...rest
}) {
  const cardStyle =
    variant === "card"
      ? {
          padding: "12px 14px",
          background: "var(--nw-surface)",
          border: "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-card)",
        }
      : null;
  return (
    <div
      data-testid={testId}
      className={`flex items-center justify-between ${className}`}
      style={{ ...cardStyle, ...style }}
      {...rest}
    >
      <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
        {invalid ? (
          <TriangleAlert size={18} style={{ color: "var(--nw-alert)", flexShrink: 0 }} aria-hidden="true" />
        ) : (
          <Check size={18} style={{ color: "var(--nw-ok)", flexShrink: 0 }} aria-hidden="true" />
        )}
        <div style={{ minWidth: 0 }}>
          {eyebrow && (
            // DirectionA2.dc.html:61 — 10px/700, 0.14em letter-spacing, uppercase.
            <div
              style={{
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--nw-ink-muted)",
              }}
            >
              {eyebrow}
            </div>
          )}
          <div className="nw-body-strong">{value}</div>
        </div>
      </div>
      {onChange && (
        <button
          type="button"
          onClick={onChange}
          data-testid={changeTestId}
          {...changeButtonProps}
          style={{
            color: "var(--nw-navy)",
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontWeight: 600,
            fontSize: 13,
            background: "none",
            border: "none",
            cursor: "pointer",
            flexShrink: 0,
            // #1309 (D221 G4): a 44px tap target. The extra height is pulled
            // back out of the card with the card's own 12px vertical padding
            // (card variant only: a plain row has no padding to absorb them),
            // so the card is no taller and the text stays right-aligned.
            minHeight: "var(--nw-size-tap)",
            minWidth: "var(--nw-size-tap)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 0 0 12px",
            ...(variant === "card" ? { marginTop: -12, marginBottom: -12 } : null),
            marginRight: 0,
          }}
        >
          {changeLabel}
        </button>
      )}
    </div>
  );
}

export default ConfirmedRow;
